import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BookOpen, ChevronRight, ChevronLeft, CheckCircle, EyeOff, Lightbulb, ShieldAlert, GripHorizontal } from 'lucide-react';
import { resolveStepContent } from './roleLearningMaps.js';

const BAR_COLORS = {
  emerald: { active: '#34d399', filled: '#10b981' },
  blue:    { active: '#60a5fa', filled: '#3b82f6' },
  amber:   { active: '#fbbf24', filled: '#f59e0b' },
  violet:  { active: '#a78bfa', filled: '#8b5cf6' },
  cyan:    { active: '#22d3ee', filled: '#06b6d4' },
  sky:     { active: '#38bdf8', filled: '#0ea5e9' },
  rose:    { active: '#fb7185', filled: '#f43f5e' },
  slate:   { active: '#94a3b8', filled: '#64748b' },
};

const POSITION_KEY = 'referralgate.learningPanel.position';

function clampToViewport(x, y, width, height) {
  const margin = 8;
  return {
    x: Math.max(margin, Math.min(x, window.innerWidth - width - margin)),
    y: Math.max(margin, Math.min(y, window.innerHeight - height - margin)),
  };
}

function loadSavedPosition() {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const pos = JSON.parse(raw);
    if (typeof pos.x === 'number' && typeof pos.y === 'number') return pos;
  } catch { /* ignore */ }
  return null;
}

export default function GuidedLearningPanel({
  module,
  roleColor,
  isStepCompleted,
  onCompleteStep,
  onCompleteModule,
  onDismiss,
  learningCtx,
  onFocusTargetChange,
  resetKey,
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const panelRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setCurrentStepIndex(0);
  }, [resetKey]);

  const stepsExist = module?.steps?.length > 0;
  const steps = stepsExist ? module.steps : [];
  const safeIndex = stepsExist ? Math.min(currentStepIndex, steps.length - 1) : 0;
  const step = stepsExist ? resolveStepContent(steps[safeIndex], learningCtx) : null;

  useEffect(() => {
    if (step?.focusTarget && onFocusTargetChange) {
      onFocusTargetChange(step.focusTarget);
    }
    return () => {
      if (onFocusTargetChange) onFocusTargetChange(null);
    };
  }, [safeIndex, step?.focusTarget, onFocusTargetChange]);

  const handlePointerDown = useCallback((e) => {
    if (!panelRef.current) return;
    e.preventDefault();
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
    if (!position) {
      setPosition({ x: rect.left, y: rect.top });
    }
  }, [position]);

  useEffect(() => {
    if (!dragging) return;

    const handlePointerMove = (e) => {
      if (!panelRef.current) return;
      const rect = panelRef.current.getBoundingClientRect();
      const clamped = clampToViewport(
        e.clientX - dragOffset.current.x,
        e.clientY - dragOffset.current.y,
        rect.width,
        rect.height,
      );
      setPosition(clamped);
    };

    const handlePointerUp = () => {
      setDragging(false);
      setPosition((pos) => {
        if (pos) {
          try { localStorage.setItem(POSITION_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
        }
        return pos;
      });
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [dragging]);

  useEffect(() => {
    setPosition(null);
    try { localStorage.removeItem(POSITION_KEY); } catch { /* ignore */ }
  }, [module?.moduleId]);

  if (!stepsExist) return null;
  const completed = isStepCompleted(module.moduleId, step.stepId);
  const isLastStep = safeIndex === steps.length - 1;
  const isFirstStep = safeIndex === 0;
  const allDone = steps.every(s => isStepCompleted(module.moduleId, s.stepId));
  const completedCount = steps.filter(s => isStepCompleted(module.moduleId, s.stepId)).length;

  const handleGotIt = () => {
    if (!completed) {
      onCompleteStep(module.moduleId, step.stepId);
    }
    if (isLastStep) {
      const willAllBeDone = completedCount + (completed ? 0 : 1) === steps.length;
      if (willAllBeDone) {
        onCompleteModule(module.moduleId);
      }
    } else {
      setCurrentStepIndex(i => i + 1);
    }
  };

  const barColors = BAR_COLORS[roleColor] || BAR_COLORS.emerald;

  const positionStyle = position
    ? { position: 'fixed', left: position.x, top: position.y, bottom: 'auto', right: 'auto' }
    : {};

  return (
    <div
      ref={panelRef}
      style={positionStyle}
      className={`
        surface-warm bg-slate-900/95 border border-${roleColor}-500/20 rounded-xl shadow-xl
        backdrop-blur-sm overflow-hidden max-w-sm w-full
        ${dragging ? 'cursor-grabbing select-none' : ''}
      `}
    >
      <div
        onPointerDown={handlePointerDown}
        className={`px-4 py-3 border-b border-slate-800/50 flex items-center justify-between cursor-grab ${dragging ? 'cursor-grabbing' : ''}`}
      >
        <div className="flex items-center gap-2">
          <GripHorizontal className="w-3.5 h-3.5 text-slate-600" />
          <BookOpen className={`w-4 h-4 text-${roleColor}-400`} />
          <span className={`text-xs font-semibold text-${roleColor}-400 uppercase tracking-wider`}>
            {/* Prefix every learning module header as "LEARN · TITLE" at render
                time so the panel reads as guidance. Underlying module.title in
                roleLearningMaps stays unchanged. Guards against duplicate prefix. */}
            {/^\s*learn\s*·/i.test(module.title || '') ? module.title : `LEARN · ${module.title}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {safeIndex + 1}/{steps.length}
          </span>
          <button
            onClick={() => onDismiss(module.moduleId)}
            className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-slate-300 transition-colors"
            title="Hide guidance"
          >
            <EyeOff className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex px-4 pt-3 gap-1">
        {steps.map((s, i) => {
          const isCurrent = i === safeIndex;
          const isFilled = i <= safeIndex || isStepCompleted(module.moduleId, s.stepId);
          return (
            <div
              key={s.stepId}
              style={isFilled ? { backgroundColor: isCurrent ? barColors.active : barColors.filled } : undefined}
              className={`h-1.5 flex-1 rounded-full transition-all duration-200 ${isFilled ? '' : 'bg-slate-700/50'}`}
            />
          );
        })}
      </div>

      <div className="px-4 py-4">
        <div className="flex items-start gap-2 mb-2">
          {completed ? (
            <CheckCircle className={`w-4 h-4 text-${roleColor}-400 mt-0.5 flex-shrink-0`} />
          ) : (
            <span className={`text-xs font-bold text-${roleColor}-400 bg-${roleColor}-500/10 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5`}>
              {safeIndex + 1}
            </span>
          )}
          <h3 className="text-sm font-semibold text-white leading-snug">{step.title}</h3>
        </div>

        <div className="text-xs text-slate-400 leading-relaxed mb-3 ml-7 space-y-2">
          {step.body.split('\n\n').map((block, i) => (
            <p key={i}>
              {block.split('\n').map((line, j, arr) => (
                <React.Fragment key={j}>
                  {line}
                  {j < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          ))}
        </div>

        {(step.teaches || step.prevents) && (
          <div className="ml-7 space-y-1.5 mb-3">
            {step.teaches && (
              <div className="flex items-start gap-1.5">
                <Lightbulb className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" title="Learn" />
                <span className="text-[11px] text-slate-500"><span className="font-semibold text-amber-400/80">Learn</span> — {step.teaches}</span>
              </div>
            )}
            {step.prevents && (
              <div className="flex items-start gap-1.5">
                <ShieldAlert className="w-3 h-3 text-red-400/60 mt-0.5 flex-shrink-0" title="Prevent" />
                <span className="text-[11px] text-slate-500"><span className="font-semibold text-red-400/60">Prevent</span> — {step.prevents}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-4 pb-3 flex items-center justify-between gap-2">
        <button
          onClick={() => setCurrentStepIndex(i => i - 1)}
          disabled={isFirstStep}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>

        <button
          onClick={handleGotIt}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
            ${allDone
              ? 'bg-slate-700 text-slate-400'
              : `bg-${roleColor}-500/20 text-${roleColor}-400 hover:bg-${roleColor}-500/30`}
            transition-colors
          `}
        >
          {completed && isLastStep
            ? 'Done'
            : completed
              ? <>Next <ChevronRight className="w-3.5 h-3.5" /></>
              : isLastStep
                ? 'Got it — finish'
                : <>Got it <ChevronRight className="w-3.5 h-3.5" /></>
          }
        </button>
      </div>
    </div>
  );
}
